import React, { useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from './Icon';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import type { IngestionImpactSheetData, IngestionImpactItem } from '../hooks/useIngestionImpactFeed';
import LearnMoreLink from './LearnMoreLink';

interface Props {
  visible: boolean;
  data: IngestionImpactSheetData | null;
  onClose: () => void;
  /** Open the underlying document. Receives the document id. Omit to hide the
   *  per-row "Open" affordance (e.g. when no navigation target is available). */
  onOpenFile?: (documentId: string) => void;
}

function CountPill({ label, count }: { label: string; count: number }) {
  const theme = useTheme();
  if (!count) return null;
  return (
    <View style={[styles.pill, { backgroundColor: theme.colors.primary + '18' }]}>
      <Text style={[styles.pillText, { color: theme.colors.primary }]}>
        +{count} {label}
      </Text>
    </View>
  );
}

const FILE_BADGES: { key: 'medications' | 'contacts' | 'events' | 'inferences' | 'memories'; label: string }[] = [
  { key: 'medications', label: 'meds' },
  { key: 'contacts', label: 'contacts' },
  { key: 'events', label: 'events' },
  { key: 'inferences', label: 'inferences' },
  { key: 'memories', label: 'memories' },
];

function FileRow({ item, onOpen }: { item: IngestionImpactItem; onOpen?: () => void }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const summary = (item.summary || '').trim();
  const badges = FILE_BADGES.map((b) => ({ label: b.label, count: item[b.key] || 0 })).filter((b) => b.count > 0);
  const longSummary = summary.length > 140;

  return (
    <View style={[styles.fileRow, { borderColor: theme.colors.border }]}>
      <View style={styles.fileRowHead}>
        <Text style={[styles.fileName, { color: theme.colors.text }]} numberOfLines={1}>
          {item.filename || 'Document'}
        </Text>
        {onOpen && (
          <TouchableOpacity
            onPress={onOpen}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={styles.openBtn}
            activeOpacity={0.7}
          >
            <Text style={[styles.openBtnText, { color: theme.colors.primary }]}>Open</Text>
            <Icon icon={['fas', 'chevron-right']} size={10} color={theme.colors.primary} />
          </TouchableOpacity>
        )}
      </View>

      {badges.length > 0 && (
        <View style={styles.badgeRow}>
          {badges.map((b) => (
            <View key={b.label} style={[styles.badge, { backgroundColor: theme.colors.primary + '14' }]}>
              <Text style={[styles.badgeText, { color: theme.colors.primary }]}>
                +{b.count} {b.label}
              </Text>
            </View>
          ))}
        </View>
      )}

      {summary.length > 0 && (
        <>
          <Text
            style={[styles.fileSummary, { color: theme.colors.muted }]}
            numberOfLines={expanded ? undefined : 2}
          >
            {summary}
          </Text>
          {longSummary && (
            <TouchableOpacity onPress={() => setExpanded((v) => !v)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
              <Text style={[styles.moreText, { color: theme.colors.primary }]}>{expanded ? 'Less' : 'More'}</Text>
            </TouchableOpacity>
          )}
        </>
      )}
    </View>
  );
}

export default function IngestionImpactSheet({ visible, data, onClose, onOpenFile }: Props) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const sheet = useSheetLayout({ desktopMaxWidth: 520 });
  const { height: windowHeight } = useWindowDimensions();

  const items = data?.items ?? [];
  const fileCount = data?.fileCount ?? items.length;
  const maxSheetHeight = Math.max(360, windowHeight - insets.top - 20);
  const headline = fileCount <= 1 ? '1 file added to your plan' : `${fileCount} files added to your plan`;

  return (
    <Modal
      visible={visible && !!data}
      transparent
      animationType={sheet.animationType}
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <Pressable style={[styles.overlay, sheet.overlayStyle]} onPress={onClose}>
        <Pressable
          style={[
            styles.sheet,
            {
              backgroundColor: theme.colors.card,
              paddingBottom: Math.max(insets.bottom, 16),
              maxHeight: maxSheetHeight,
            },
            sheet.sheetStyle,
            sheet.desktopContentStyle,
          ]}
          onPress={() => {}}
        >
          {sheet.showHandle && <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />}
          <View style={styles.header}>
            <View style={[styles.iconWrap, { backgroundColor: theme.colors.primary + '18' }]}>
              <Icon icon={['fad', 'sparkles']} size={15} color={theme.colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.title, { color: theme.colors.text }]}>Knowledge updated</Text>
              <Text style={[styles.subtitle, { color: theme.colors.muted }]}>{headline}</Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Icon icon={['fas', 'xmark']} size={16} color={theme.colors.muted} />
            </TouchableOpacity>
          </View>

          <View style={styles.pillRow}>
            <CountPill label="contacts" count={data?.contacts || 0} />
            <CountPill label="meds" count={data?.medications || 0} />
            <CountPill label="events" count={data?.events || 0} />
            <CountPill label="inferences" count={data?.inferences || 0} />
            <CountPill label="memories" count={data?.memories || 0} />
          </View>

          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator
            nestedScrollEnabled
          >
            {items.map((item) => (
              <FileRow
                key={item.documentId}
                item={item}
                onOpen={onOpenFile ? () => onOpenFile(item.documentId) : undefined}
              />
            ))}
          </ScrollView>

          <LearnMoreLink slug="how-eleplan-reads-documents" align="center" />

          <TouchableOpacity
            style={[styles.doneBtn, { backgroundColor: theme.colors.primary }]}
            onPress={onClose}
            activeOpacity={0.8}
          >
            <Text style={styles.doneBtnText}>Done</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.42)',
    justifyContent: 'flex-end',
  },
  sheet: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 10,
    paddingHorizontal: 16,
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
  },
  subtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 12,
  },
  pill: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  pillText: {
    fontSize: 12,
    fontWeight: '600',
  },
  list: {
    marginTop: 12,
  },
  listContent: {
    paddingBottom: 4,
    gap: 8,
  },
  fileRow: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 10,
    gap: 6,
  },
  fileRowHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  fileName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },
  openBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  openBtnText: {
    fontSize: 13,
    fontWeight: '600',
  },
  badgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
  },
  badge: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  fileSummary: {
    fontSize: 13,
    lineHeight: 19,
  },
  moreText: {
    fontSize: 12,
    fontWeight: '600',
  },
  doneBtn: {
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
  },
  doneBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
});
